import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaFacebook, FaLinkedin, FaInstagram } from 'react-icons/fa';
import { SOLUTIONS } from '../../data/solutionsMenu';

const FooterLink = ({ to, children }) => {
  const location = useLocation();
  const isActive = location.pathname === to;
  
  return (
    <li>
      <Link 
        to={to} 
        className={`text-sm transition-colors ${isActive ? 'text-violet-600 font-semibold' : 'text-gray-600 hover:text-violet-600'}`}
      >
        {children}
      </Link>
    </li>
  );
};

const Footer = () => {
  return (
    <footer className="bg-white border-t border-gray-200 mt-auto">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8">
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <img src="/logo.svg" alt="Appozy" className="w-8 h-8" />
              <span className="text-xl font-extrabold text-gray-900 tracking-tight">Appozy</span>
            </div>
            <p className="text-gray-600 text-sm">
              Smart booking and scheduling platform for professionals and businesses of all sizes.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-gray-900 mb-4">Product</h4>
            <ul className="space-y-2 text-sm">
              <FooterLink to="/features">Features</FooterLink>
              <FooterLink to="/pricing">Pricing</FooterLink>
              <FooterLink to="/integrations">Integrations</FooterLink>
              <FooterLink to="/changelog">Changelog</FooterLink>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-gray-900 mb-4">Solutions</h4>
            <ul className="space-y-2 text-sm">
              {SOLUTIONS.map((s) => (
                <FooterLink key={s.to} to={s.to}>{s.label}</FooterLink>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-gray-900 mb-4">Resources</h4>
            <ul className="space-y-2 text-sm">
              <FooterLink to="/install">Install on your device</FooterLink>
              <FooterLink to="/docs">Documentation</FooterLink>
              <FooterLink to="/api">API Reference</FooterLink>
              <FooterLink to="/support">Support Center</FooterLink>
              <FooterLink to="/contact">Contact Us</FooterLink>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-gray-900 mb-4">Company</h4>
            <ul className="space-y-2 text-sm">
              <FooterLink to="/about">About</FooterLink>
              <FooterLink to="/careers">Careers</FooterLink>
              <FooterLink to="/privacy">Privacy Policy</FooterLink>
              <FooterLink to="/terms">Terms of Service</FooterLink>
              <FooterLink to="/refund">Refund Policy</FooterLink>
              <FooterLink to="/cookies">Cookie Policy</FooterLink>
            </ul>
          </div>
        </div>

        <div className="mt-8 pt-8 border-t border-gray-200 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-gray-500 text-center md:text-left">
            © {new Date().getFullYear()} Appozy. All rights reserved. A product by{' '}
            <a href="https://hoffstee.com.au" target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:text-violet-700">
              Hoffstee
            </a>
          </p>

          {/* Trust badges — direct link to our G2 listing. Reinforces brand
              recognition (helps Google stop autocorrecting "appozy" to
              "epoxy") and gives buyers a path to read independent reviews.
              Custom-built badge using G2's orange brand colour so it loads
              instantly with zero third-party script weight. */}
          <a
            href="https://www.g2.com/products/appozy/reviews"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Appozy on G2 — read reviews"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 hover:border-orange-300 hover:bg-orange-50/50 transition-all group"
          >
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#FF492C] text-white text-[10px] font-extrabold leading-none">
              G²
            </span>
            <span className="text-xs font-semibold text-gray-600 group-hover:text-orange-700">
              Listed on G2
            </span>
          </a>

          <div className="flex space-x-4 mt-4 md:mt-0">
            <a href="https://www.linkedin.com/company/hoffstee/" target="_blank" rel="noopener noreferrer" aria-label="Hoffstee on LinkedIn" className="text-gray-400 hover:text-violet-600 transition-colors">
              <FaLinkedin size={20} />
            </a>
            <a href="https://www.instagram.com/hoffstee_wd/" target="_blank" rel="noopener noreferrer" aria-label="Hoffstee on Instagram" className="text-gray-400 hover:text-violet-600 transition-colors">
              <FaInstagram size={20} />
            </a>
            <a href="https://www.facebook.com/Hoffstee" target="_blank" rel="noopener noreferrer" aria-label="Hoffstee on Facebook" className="text-gray-400 hover:text-violet-600 transition-colors">
              <FaFacebook size={20} />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;